import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Edit2, Users, Trash2, Shield } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState } from '../../components/common/EmptyState';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useGetRolesQuery, useDeleteRoleMutation } from '../../store/slices/apiSlice';
import type { RoleSummary, RoleListPageProps } from '../../types';
import { useToast } from '../../hooks/useToast';

export const RoleListPage: React.FC<RoleListPageProps> = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('name');
  const toast = useToast();

  const {
    data: rolesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetRolesQuery({
    page: currentPage,
    page_size: 20,
    search: searchTerm || undefined,
    is_active: activeFilter,
    ordering: sortBy,
  });

  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleDeleteRole = async (role: RoleSummary) => {
    if (role.user_count > 0) {
      toast.warning(
        'Cannot Delete Role',
        `Role "${role.name}" is assigned to ${role.user_count} users. Please reassign users before deleting.`
      );
      return;
    }

    if (window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`)) {
      try {
        await deleteRole(role.id).unwrap();
        toast.success(
          'Role Deleted',
          `Role "${role.name}" has been successfully deleted.`
        );
        refetch();
      } catch (error: any) {
        toast.error(
          'Failed to Delete Role',
          error.data?.detail || 'An unknown error occurred while deleting the role.'
        );
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (!rolesData || rolesData.count <= 20) return null;

    const totalPages = Math.ceil(rolesData.count / 20);
    const pageNumbers = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, rolesData.count)} of {rolesData.count} roles
        </div>
        <div className="flex space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          {pageNumbers.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <AppShell>
      <PageLayout
        title="Role Management"
        subtitle="Manage system roles and permissions"
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<Shield className="w-6 h-6" />}
      >
        <ContentCard
          title="System Roles"
          subtitle={`${rolesData?.count || 0} roles total`}
          actions={
            <Link to="/roles/new">
              <Button icon={<Plus className="w-4 h-4" />}>
                Create Role
              </Button>
            </Link>
          }
        >
          {/* Search and Filter Controls */}
          <div className="mb-6 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search roles by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>
            
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <select
                value={activeFilter === undefined ? 'all' : activeFilter.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setActiveFilter(value === 'all' ? undefined : value === 'true');
                  setCurrentPage(1);
                }}
                className="form-input w-auto"
              >
                <option value="all">All Roles</option>
                <option value="true">Active Only</option>
                <option value="false">Inactive Only</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input w-auto"
              >
                <option value="name">Sort by Name</option>
                <option value="-name">Sort by Name (Z-A)</option>
                <option value="user_count">Sort by User Count</option>
                <option value="-user_count">Sort by User Count (High-Low)</option>
                <option value="created_at">Sort by Date Created</option>
                <option value="-created_at">Sort by Date Created (Newest)</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className={`alert mb-6 ${
              'status' in error && error.status === 403 
                ? 'alert-warning' 
                : 'alert-error'
            }`}>
              <div className="flex items-center">
                {('status' in error && error.status === 403) ? (
                  <Shield className="w-5 h-5 mr-2" />
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {('status' in error && error.status === 403) 
                  ? "You don't have permission to view roles. Please contact your administrator to request the required 'roles_view' permission."
                  : "Failed to load roles. Please try again."
                }
                {!('status' in error && error.status === 403) && (
                  <Button variant="ghost" size="sm" onClick={() => refetch()} className="ml-2">
                    Retry
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && rolesData?.results.length === 0 && (
            <EmptyState
              title="No roles found"
              description={searchTerm ? "Try adjusting your search terms or filters." : "Get started by creating your first role."}
              action={
                <Link to="/roles/new">
                  <Button icon={<Plus className="w-4 h-4" />}>
                    Create Role
                  </Button>
                </Link>
              }
            />
          )}

          {/* Roles Table */}
          {!isLoading && !isError && rolesData && rolesData.results.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rolesData.results.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {role.name}
                            </div>
                            <div className="text-sm text-gray-500 max-w-md truncate">
                              {role.description}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Users className="w-4 h-4 mr-1 text-gray-400" />
                            {role.user_count}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            role.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {role.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Link to={`/roles/${role.id}`}>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </Link>
                            <Link to={`/roles/${role.id}/edit`}>
                              <Button variant="ghost" size="sm" icon={<Edit2 className="w-4 h-4" />}>
                                Edit
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Trash2 className="w-4 h-4" />}
                              onClick={() => handleDeleteRole(role)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {renderPagination()}
            </>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
